import React, { useState, useRef } from 'react';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import { parseHeaders, waitForUrl } from 'legacy-src/utility/utils';
import S3Upload from './s3upload.js';

import './FileUpload.scss';
import PropTypes from 'prop-types';

export const UPLOAD_FILE_TYPES = {
  IMAGE: 'image/*',
  AUDIO: 'audio/*',
};

const defaultUrl = `${CB_LEGACY_URL}/n/api/sign_upload`;

const FileUpload = ({
  onProgress,
  onError,
  onUploaded,
  onProcessed,
  getProcessedURL,
  url = defaultUrl,
  fileType,
  allowedFileExtensions,
  ...props
}) => {
  const [status, setStatus] = useState({
    uploading: false,
    uploadStatus: '',
    uploadComplete: false,
    uploadError: false,
  });
  const uploadInputRef = useRef(null);

  const handleProgress = (percent, message) => {
    // status could be words sometimes, so only add the % if numeric.
    setStatus({
      uploading: true,
      uploadStatus: [percent || 0, '%'].join(''),
      uploadError: false,
    });

    onProgress && onProgress(percent, message);
  };

  const handleFinish = (result, headerString) => {
    const headers = parseHeaders(headerString);
    const { etag } = headers;

    setStatus({ uploadStatus: 'Processing...' });
    onUploaded && onUploaded(result, etag);

    Promise.resolve(
      onProcessed &&
        getProcessedURL &&
        waitForUrl(getProcessedURL(result, etag)).then(() => {
          onProcessed(result, etag);
        }),
    ).then(() => {
      setStatus({
        uploading: false,
        uploadStatus: 'Complete!',
        uploadComplete: true,
        uploadError: false,
      });
    });
  };

  const handleError = () => {
    setStatus({
      uploading: false,
      uploadStatus: 'Failed.',
    });
    onError && onError();
  };

  // don't use this anywhere else, it should be using axios or apollo to handle jwt tokens (and refreshing). However
  // I don't want to go through refactoring client/src/components/content/controls/s3upload.js
  // It looks like any of the files that import the FileUpload export are never used in ui-fym anyway. Should be cleaned
  // up but I'm not committing to that -James
  const getAuthHeader = () => {
    let headers = {};
    const token = window.localStorage.getItem('account_access_token');
    if (token && token !== 'undefined') {
      headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
    return headers;
  };

  function isValidFileType() {
    if (allowedFileExtensions && allowedFileExtensions.length) {
      const filePath = uploadInputRef.current.value;
      const extension = filePath.split('.').pop();
      return allowedFileExtensions.includes(extension.toLowerCase());
    }
    return true;
  }

  const handleUploadFile = () => {
    if (isValidFileType()) {
      return new S3Upload({
        fileElement: uploadInputRef.current,
        signingUrl: url,
        onProgress: handleProgress,
        onFinishS3Put: handleFinish,
        onError: handleError,
        signingUrlHeaders: getAuthHeader(),
        //https://stackoverflow.com/questions/47654527/aws-s3-server-side-encryption-access-denied-error/47660402
        uploadRequestHeaders: { 'x-amz-acl': 'bucket-owner-full-control' },
      });
    }
    setStatus({
      uploading: false,
      uploadStatus: `File type not allowed. Allowed file types: ${allowedFileExtensions.join(
        ', ',
      )}`,
      uploadError: true,
    });
  };

  return (
    <div className="file_upload">
      <input
        type="file"
        accept={fileType}
        onChange={handleUploadFile}
        ref={uploadInputRef}
        {...props}
      />
      {status.uploading && <LoadingSpinner message={status.uploadStatus} />}
      {status.uploadStatus === 'Complete!' && status.uploadStatus}
      {status.uploadError && (
        <span className="error-text">{status.uploadStatus}</span>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  fileType: PropTypes.string.isRequired,
};

export default FileUpload;
